/**
 * Panel 9: Real-Time Tactical — Live metrics + stress tests + model info
 *
 * Feature vectors are constructed from REAL asset operational data
 * using the C2 20-variable schema (see FEATURE_LABELS).
 */

import { useState, useEffect, useCallback, Fragment } from 'react'
import type { AssetSummary, AssetDetail, ModelInfo, EdgeStatus } from '../../../types'
import { getForceComposition, getAssetDetail, getModelInfo, predict, formatPercent } from '../../../services/api'
import { TEMPO_CONFIG, type Tempo } from '../../../types'
import { LossSimulatorView } from './loss-simulator/LossSimulatorView'

type TacticalMode = 'predict' | 'loss-sim'

// ===== C2 20-Variable Feature Schema =====
const FEATURE_LABELS: string[] = [
  /* 0  */ 'sorties_generated',
  /* 1  */ 'missions_effective',
  /* 2  */ 'isr_hours_flown',
  /* 3  */ 'flight_hours_consumed',
  /* 4  */ 'mission_surplus',
  /* 5  */ 'objectives_achieved',
  /* 6  */ 'assets_available',
  /* 7  */ 'assets_in_maintenance',
  /* 8  */ 'munitions_on_hand',
  /* 9  */ 'total_force_structure',
  /* 10 */ 'committed_taskings',
  /* 11 */ 'uncommitted_capacity',
  /* 12 */ 'scheduled_missions_24h',
  /* 13 */ 'scheduled_missions_72h',
  /* 14 */ 'mission_effectiveness_rate',
  /* 15 */ 'net_available_capacity',
  /* 16 */ 'capacity_utilization_ratio',
  /* 17 */ 'tasking_load_ratio',
  /* 18 */ 'munitions_depth_ratio',
  /* 19 */ 'schedule_density_ratio',
]

/**
 * Build a 20-element feature vector from real asset operational data.
 * Uses AssetDetail (which includes operational_metrics + fingerprint)
 * to compute every variable deterministically.
 */
function buildFeatureVector(detail: AssetDetail): number[] {
  const om = detail.operational_metrics
  const fp = detail.fingerprint
  const count = detail.assigned_count

  const assets_available = om.assets_available                          // [6]
  const assets_in_maintenance = om.assets_in_maintenance                // [7]
  const total_force = count                                             // [9]
  const sorties = om.sorties_per_period                                 // [0]
  const mission_eff = om.mission_effectiveness                          // [14]

  // [0] sorties_generated — direct from operational_metrics
  const sorties_generated = sorties

  // [1] missions_effective — sorties * mission_effectiveness
  const missions_effective = sorties * mission_eff

  // [2] isr_hours_flown — estimate from role: ISR/RPA types fly more ISR hours
  //     Use fingerprint.mission as a proxy scaled by available assets
  const isr_hours_flown = (fp.mission / 100) * assets_available * 2.5

  // [3] flight_hours_consumed — sorties * average turnaround (in hours) gives a rough proxy
  const turnaround_hrs = om.turnaround_min / 60
  const flight_hours_consumed = sorties * turnaround_hrs

  // [4] mission_surplus — effective missions minus committed taskings (can be negative)
  const committed_taskings = Math.round(sorties * (fp.readiness / 100) * 0.8) // [10]
  const mission_surplus = missions_effective - committed_taskings

  // [5] objectives_achieved — readiness_score scaled to a 0-100 metric
  const objectives_achieved = detail.readiness_score * mission_eff

  // [6] assets_available — from operational_metrics
  // [7] assets_in_maintenance — from operational_metrics

  // [8] munitions_on_hand — estimate: available assets * logistics fingerprint node
  const munitions_on_hand = assets_available * (fp.logistics / 100) * 15

  // [9] total_force_structure — assigned_count

  // [10] committed_taskings — computed above

  // [11] uncommitted_capacity — available minus committed fraction
  const uncommitted_capacity = Math.max(0, assets_available - committed_taskings * 0.3)

  // [12] scheduled_missions_24h — fraction of sorties in a 24h window
  const scheduled_missions_24h = Math.round(sorties * 0.6)

  // [13] scheduled_missions_72h — 3x the 24h with some ramp
  const scheduled_missions_72h = Math.round(sorties * 1.6)

  // [14] mission_effectiveness_rate — direct
  const mission_effectiveness_rate = mission_eff

  // [15] net_available_capacity — assets_available - committed_taskings * load factor
  const net_available_capacity = Math.max(0, assets_available - committed_taskings * 0.25)

  // [16] capacity_utilization_ratio — committed / available
  const capacity_utilization_ratio = assets_available > 0
    ? Math.min(1.0, committed_taskings / assets_available)
    : 0

  // [17] tasking_load_ratio — committed / total_force
  const tasking_load_ratio = total_force > 0
    ? Math.min(1.0, committed_taskings / total_force)
    : 0

  // [18] munitions_depth_ratio — munitions_on_hand / (committed_taskings * 3 + 1)
  const munitions_depth_ratio = munitions_on_hand / (committed_taskings * 3 + 1)

  // [19] schedule_density_ratio — scheduled_24h / (available + 1)
  const schedule_density_ratio = scheduled_missions_24h / (assets_available + 1)

  const vec: number[] = [
    sorties_generated,          // 0
    missions_effective,         // 1
    isr_hours_flown,            // 2
    flight_hours_consumed,      // 3
    mission_surplus,            // 4
    objectives_achieved,        // 5
    assets_available,           // 6
    assets_in_maintenance,      // 7
    munitions_on_hand,          // 8
    total_force,                // 9
    committed_taskings,         // 10
    uncommitted_capacity,       // 11
    scheduled_missions_24h,     // 12
    scheduled_missions_72h,     // 13
    mission_effectiveness_rate, // 14
    net_available_capacity,     // 15
    capacity_utilization_ratio, // 16
    tasking_load_ratio,         // 17
    munitions_depth_ratio,      // 18
    schedule_density_ratio,     // 19
  ]

  // Round to 4 decimal places for cleanliness
  return vec.map(v => Math.round(v * 10000) / 10000)
}

// ===== What-If Scenario Presets =====
interface ScenarioPreset {
  id: string
  name: string
  description: string
  color: string
  modifiers: Record<number, (v: number) => number> // feature index → transform
}

const SCENARIO_PRESETS: ScenarioPreset[] = [
  {
    id: 'baseline',
    name: 'BASELINE',
    description: 'Current operational posture — no modifications',
    color: 'text-term-success',
    modifiers: {},
  },
  {
    id: 'surge',
    name: 'SURGE OPS',
    description: 'Increase sortie generation 80%, committed taskings 2x, deplete munitions 40%',
    color: 'text-term-warning',
    modifiers: {
      0: v => v * 1.8,                 // sorties +80%
      1: v => v * 1.6,                 // missions effective +60%
      3: v => v * 1.8,                 // flight hours +80%
      8: v => v * 0.6,                 // munitions -40%
      10: v => v * 2.0,                // committed taskings 2x
      12: v => v * 2.0,               // scheduled 24h 2x
      13: v => v * 1.8,               // scheduled 72h +80%
    },
  },
  {
    id: 'attrition',
    name: 'ATTRITION',
    description: 'Lose 30% aircraft to battle damage, MC rate collapse, maintenance surge',
    color: 'text-term-error',
    modifiers: {
      6: v => v * 0.7,                 // available assets -30%
      7: v => v * 1.8,                 // in maintenance +80%
      9: v => v * 0.85,                // total force -15% (some destroyed)
      11: v => Math.max(0, v * 0.4),   // uncommitted capacity -60%
      14: v => v * 0.75,               // mission effectiveness -25%
      15: v => Math.max(0, v * 0.5),   // net available -50%
    },
  },
  {
    id: 'mass_casualty',
    name: 'MASS CAS',
    description: 'Catastrophic event — 50% fleet unavailable, depot overwhelmed',
    color: 'text-red-400',
    modifiers: {
      0: v => v * 0.4,                 // sorties -60%
      1: v => v * 0.3,                 // missions effective -70%
      6: v => v * 0.5,                 // available -50%
      7: v => v * 2.5,                 // maintenance surge
      8: v => v * 0.3,                 // munitions depleted
      9: v => v * 0.7,                 // force structure -30%
      11: () => 0,                      // zero uncommitted
      14: v => v * 0.5,               // effectiveness halved
      15: () => 0,                      // zero net available
    },
  },
  {
    id: 'reconstitution',
    name: 'RECONST',
    description: 'Recovery phase — depot flow resuming, gradual aircraft return',
    color: 'text-cyan-400',
    modifiers: {
      0: v => v * 0.6,                 // reduced sorties (conserving)
      6: v => v * 0.85,                // assets recovering
      7: v => v * 1.3,                 // still elevated maintenance
      8: v => v * 1.2,                 // munitions resupply
      10: v => v * 0.5,               // reduced taskings
      12: v => v * 0.5,               // reduced schedule
      13: v => v * 0.7,               // 72h partially loaded
      14: v => v * 0.9,               // slightly reduced effectiveness
    },
  },
]

function applyScenario(baseFeatures: number[], scenario: ScenarioPreset): number[] {
  return baseFeatures.map((val, idx) => {
    const modifier = scenario.modifiers[idx]
    return modifier ? Math.round(modifier(val) * 10000) / 10000 : val
  })
}

// ===== Prediction result type =====
interface PredictionResultEntry {
  asset: string
  assetId: string
  capacity: number
  confidence_interval: [number, number]
  tempo: string
  tempoConfidence: number
  threat: string
  threatScore: number
  model: string
  time_ms: number
  features: number[]
  scenario?: string
}

// ===== Component Props =====
interface TacticalPicturePanelProps {
  edgeStatus: EdgeStatus | null
}

export function TacticalPicturePanel({ edgeStatus }: TacticalPicturePanelProps) {
  const [mode, setMode] = useState<TacticalMode>('predict')

  const [assets, setAssets] = useState<AssetSummary[]>([])
  const [assetDetails, setAssetDetails] = useState<Record<string, AssetDetail>>({})
  const [models, setModels] = useState<ModelInfo[]>([])
  const [totalEdgeMB, setTotalEdgeMB] = useState(0)
  const [ensembleWeights, setEnsembleWeights] = useState<Record<string, Record<string, number>>>({})
  const [loading, setLoading] = useState(true)

  // Single prediction result (when clicking one asset)
  const [predictionResult, setPredictionResult] = useState<PredictionResultEntry | null>(null)
  const [predicting, setPredicting] = useState(false)
  const [predictingAssetId, setPredictingAssetId] = useState<string | null>(null)

  // Predict All results
  const [allResults, setAllResults] = useState<PredictionResultEntry[]>([])
  const [predictingAll, setPredictingAll] = useState(false)

  // Feature vector visibility (per asset id)
  const [expandedFeatures, setExpandedFeatures] = useState<string | null>(null)

  // What-if scenario
  const [activeScenario, setActiveScenario] = useState<ScenarioPreset>(SCENARIO_PRESETS[0])

  // Scenario comparison (baseline vs scenario side-by-side)
  const [comparisonResults, setComparisonResults] = useState<{
    baseline: PredictionResultEntry[]
    scenario: PredictionResultEntry[]
    scenarioName: string
  } | null>(null)
  const [runningComparison, setRunningComparison] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    const [forceRes, modelRes] = await Promise.all([getForceComposition(), getModelInfo()])

    if (forceRes.data) {
      setAssets(forceRes.data.assets)

      // Fetch all asset details in parallel for operational_metrics
      const detailPromises = forceRes.data.assets.map(a =>
        getAssetDetail(a.id).then(res => ({ id: a.id, detail: res.data }))
      )
      const detailResults = await Promise.all(detailPromises)
      const detailMap: Record<string, AssetDetail> = {}
      for (const { id, detail } of detailResults) {
        if (detail) detailMap[id] = detail
      }
      setAssetDetails(detailMap)
    }

    if (modelRes.data) {
      setModels(modelRes.data.models)
      setTotalEdgeMB(modelRes.data.total_edge_size_mb)
      setEnsembleWeights(modelRes.data.ensemble_weights)
    }

    setLoading(false)
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // Run prediction for a single asset (with active scenario applied)
  const runPrediction = useCallback(async (asset: AssetSummary) => {
    const detail = assetDetails[asset.id]
    if (!detail) return

    setPredicting(true)
    setPredictingAssetId(asset.id)

    const baseFeatures = buildFeatureVector(detail)
    const features = applyScenario(baseFeatures, activeScenario)
    const res = await predict(asset.asset_type, features)

    if (res.data) {
      setPredictionResult({
        asset: asset.asset_type,
        assetId: asset.id,
        capacity: res.data.predicted_capacity,
        confidence_interval: res.data.confidence_interval as [number, number],
        tempo: res.data.detected_tempo,
        tempoConfidence: res.data.tempo_confidence,
        threat: res.data.threat_level,
        threatScore: res.data.threat_score,
        model: res.data.model_used,
        time_ms: res.data.prediction_time_ms,
        features,
        scenario: activeScenario.name,
      })
    }
    setPredicting(false)
    setPredictingAssetId(null)
  }, [assetDetails, activeScenario])

  // Run predictions for ALL assets (with active scenario applied)
  const runPredictAll = useCallback(async () => {
    setPredictingAll(true)
    setAllResults([])
    setComparisonResults(null)

    const results: PredictionResultEntry[] = []

    for (const asset of assets) {
      const detail = assetDetails[asset.id]
      if (!detail) continue

      const baseFeatures = buildFeatureVector(detail)
      const features = applyScenario(baseFeatures, activeScenario)
      const res = await predict(asset.asset_type, features)

      if (res.data) {
        results.push({
          asset: asset.asset_type,
          assetId: asset.id,
          capacity: res.data.predicted_capacity,
          confidence_interval: res.data.confidence_interval as [number, number],
          tempo: res.data.detected_tempo,
          tempoConfidence: res.data.tempo_confidence,
          threat: res.data.threat_level,
          threatScore: res.data.threat_score,
          model: res.data.model_used,
          time_ms: res.data.prediction_time_ms,
          features,
          scenario: activeScenario.name,
        })
      }
    }

    setAllResults(results)
    setPredictingAll(false)
  }, [assets, assetDetails, activeScenario])

  // Run scenario comparison: baseline vs selected scenario for ALL assets
  const runComparison = useCallback(async () => {
    if (activeScenario.id === 'baseline') return
    setRunningComparison(true)
    setComparisonResults(null)
    setAllResults([])

    const baseline: PredictionResultEntry[] = []
    const scenario: PredictionResultEntry[] = []

    for (const asset of assets) {
      const detail = assetDetails[asset.id]
      if (!detail) continue

      const baseFeatures = buildFeatureVector(detail)

      // Run baseline
      const baseRes = await predict(asset.asset_type, baseFeatures)
      if (baseRes.data) {
        baseline.push({
          asset: asset.asset_type, assetId: asset.id,
          capacity: baseRes.data.predicted_capacity,
          confidence_interval: baseRes.data.confidence_interval as [number, number],
          tempo: baseRes.data.detected_tempo, tempoConfidence: baseRes.data.tempo_confidence,
          threat: baseRes.data.threat_level, threatScore: baseRes.data.threat_score,
          model: baseRes.data.model_used, time_ms: baseRes.data.prediction_time_ms,
          features: baseFeatures, scenario: 'BASELINE',
        })
      }

      // Run scenario
      const scenFeatures = applyScenario(baseFeatures, activeScenario)
      const scenRes = await predict(asset.asset_type, scenFeatures)
      if (scenRes.data) {
        scenario.push({
          asset: asset.asset_type, assetId: asset.id,
          capacity: scenRes.data.predicted_capacity,
          confidence_interval: scenRes.data.confidence_interval as [number, number],
          tempo: scenRes.data.detected_tempo, tempoConfidence: scenRes.data.tempo_confidence,
          threat: scenRes.data.threat_level, threatScore: scenRes.data.threat_score,
          model: scenRes.data.model_used, time_ms: scenRes.data.prediction_time_ms,
          features: scenFeatures, scenario: activeScenario.name,
        })
      }
    }

    setComparisonResults({ baseline, scenario, scenarioName: activeScenario.name })
    setRunningComparison(false)
  }, [assets, assetDetails, activeScenario])

  const toggleFeatureExpand = (id: string) => {
    setExpandedFeatures(prev => prev === id ? null : id)
  }

  if (loading && mode === 'predict') return <div className="animate-pulse h-full bg-term-bgLight rounded" />

  return (
    <div className="flex flex-col gap-4 h-full overflow-auto">
      {/* Mode toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setMode('predict')}
          className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded border transition-colors
            ${mode === 'predict'
              ? 'bg-term-primary/20 border-term-primary text-term-primary'
              : 'bg-term-bg border-term-border text-term-textDim hover:border-term-primary/50 hover:text-term-text'}`}
        >
          Predict Mode
        </button>
        <button
          onClick={() => setMode('loss-sim')}
          className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded border transition-colors
            ${mode === 'loss-sim'
              ? 'bg-red-500/20 border-red-500 text-red-400'
              : 'bg-term-bg border-term-border text-term-textDim hover:border-red-500/50 hover:text-term-text'}`}
        >
          Loss Simulator
        </button>
      </div>

      {mode === 'loss-sim' ? (
        <LossSimulatorView />
      ) : (
      <>
      {/* Edge compute summary */}
      <div className="grid grid-cols-4 gap-3">
        <div className="term-box p-3 text-center">
          <div className="text-[10px] text-term-textDim uppercase">Models</div>
          <div className="text-lg font-bold text-term-text">{models.length}</div>
        </div>
        <div className="term-box p-3 text-center">
          <div className="text-[10px] text-term-textDim uppercase">Edge Size</div>
          <div className="text-lg font-bold text-term-text">{totalEdgeMB}MB</div>
        </div>
        <div className="term-box p-3 text-center">
          <div className="text-[10px] text-term-textDim uppercase">Connectivity</div>
          <div className={`text-lg font-bold ${edgeStatus?.connectivity === 'FULL' ? 'text-term-success' : 'text-term-warning'}`}>
            {edgeStatus?.connectivity ?? 'FULL'}
          </div>
        </div>
        <div className="term-box p-3 text-center">
          <div className="text-[10px] text-term-textDim uppercase">Predictions</div>
          <div className="text-lg font-bold text-term-text tabular-nums">{edgeStatus?.edge_predictions_count ?? 0}</div>
        </div>
      </div>

      {/* Model cards */}
      <div className="term-box p-3">
        <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">5-Model Ensemble</div>
        <div className="grid grid-cols-5 gap-2">
          {models.map(m => (
            <div key={m.name} className="term-box p-2 text-center">
              <div className="text-xs font-bold text-term-text">{m.name}</div>
              <div className="text-[10px] text-term-textDim mt-1">{m.type}</div>
              <div className="text-[10px] text-term-primary mt-1">{m.size_mb}MB</div>
              <div className={`text-[9px] mt-1 ${m.edge_capable ? 'text-term-success' : 'text-term-error'}`}>
                {m.edge_capable ? 'EDGE OK' : 'CLOUD ONLY'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Ensemble weights by tempo */}
      {Object.keys(ensembleWeights).length > 0 && (
        <div className="term-box p-3">
          <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">Ensemble Weights by Tempo</div>
          <table className="term-table text-xs">
            <thead>
              <tr>
                <th>Tempo</th>
                <th className="text-right">TRM</th>
                <th className="text-right">XGB</th>
                <th className="text-right">Prophet</th>
                <th className="text-right">MC-Pred</th>
                <th className="text-right">Tempo-Cls</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(ensembleWeights).map(([tempo, weights]) => {
                const cfg = TEMPO_CONFIG[tempo as Tempo]
                return (
                  <tr key={tempo}>
                    <td><span className={`${cfg?.cssClass ?? 'tempo-steady'} px-2 py-0.5 rounded text-[10px]`}>{tempo}</span></td>
                    <td className="text-right tabular-nums">{formatPercent(weights.trm)}</td>
                    <td className="text-right tabular-nums">{formatPercent(weights.xgb)}</td>
                    <td className="text-right tabular-nums">{formatPercent(weights.prophet)}</td>
                    <td className="text-right tabular-nums">{formatPercent(weights.mc_pred)}</td>
                    <td className="text-right tabular-nums">{formatPercent(weights.tempo_cls)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* What-If Scenario Selector */}
      <div className="term-box p-3">
        <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">
          What-If Scenario Engine
        </div>
        <div className="flex flex-wrap gap-2 mb-2">
          {SCENARIO_PRESETS.map(s => (
            <button
              key={s.id}
              onClick={() => { setActiveScenario(s); setAllResults([]); setComparisonResults(null); setPredictionResult(null) }}
              className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded border transition-colors
                ${activeScenario.id === s.id
                  ? 'bg-term-primary/20 border-term-primary text-term-primary'
                  : 'bg-term-bg border-term-border text-term-textDim hover:border-term-primary/50 hover:text-term-text'
                }`}
            >
              {s.name}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <div className="text-[11px] text-term-textDim">
            <span className={activeScenario.color + ' font-bold'}>{activeScenario.name}</span>
            {' — '}{activeScenario.description}
          </div>
          {activeScenario.id !== 'baseline' && (
            <button
              onClick={runComparison}
              disabled={runningComparison || predictingAll || predicting}
              className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded shrink-0 ml-3
                         bg-term-error/20 text-term-error border border-term-error/40
                         hover:bg-term-error/30 transition-colors
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {runningComparison ? 'Comparing...' : 'Compare vs Baseline'}
            </button>
          )}
        </div>
      </div>

      {/* Scenario Comparison Results */}
      {comparisonResults && (
        <div className="term-box p-3">
          <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">
            Scenario Comparison: BASELINE vs {comparisonResults.scenarioName}
          </div>
          <div className="overflow-x-auto max-h-[280px] overflow-y-auto">
            <table className="term-table text-xs w-full">
              <thead className="sticky top-0 bg-term-bg">
                <tr>
                  <th className="text-left">Asset</th>
                  <th className="text-right">Base Cap</th>
                  <th className="text-right">Scen Cap</th>
                  <th className="text-right">Delta</th>
                  <th className="text-center">Base Tempo</th>
                  <th className="text-center">Scen Tempo</th>
                  <th className="text-center">Base Threat</th>
                  <th className="text-center">Scen Threat</th>
                </tr>
              </thead>
              <tbody>
                {comparisonResults.baseline.map((b, i) => {
                  const s = comparisonResults.scenario[i]
                  if (!s) return null
                  const delta = s.capacity - b.capacity
                  const tempoChanged = b.tempo !== s.tempo
                  const threatChanged = b.threat !== s.threat
                  return (
                    <tr key={b.assetId} className={threatChanged || tempoChanged ? 'bg-term-error/5' : ''}>
                      <td className="text-left font-bold text-term-text whitespace-nowrap text-[11px]">{b.asset}</td>
                      <td className="text-right tabular-nums">{b.capacity.toFixed(1)}</td>
                      <td className="text-right tabular-nums font-bold">{s.capacity.toFixed(1)}</td>
                      <td className={`text-right tabular-nums font-bold ${delta < 0 ? 'text-term-error' : delta > 0 ? 'text-term-success' : 'text-term-textDim'}`}>
                        {delta > 0 ? '+' : ''}{delta.toFixed(1)}
                      </td>
                      <td className="text-center">
                        <span className={`${TEMPO_CONFIG[b.tempo as Tempo]?.cssClass ?? ''} px-1 py-0.5 rounded text-[9px]`}>{b.tempo}</span>
                      </td>
                      <td className="text-center">
                        <span className={`${TEMPO_CONFIG[s.tempo as Tempo]?.cssClass ?? ''} px-1 py-0.5 rounded text-[9px] ${tempoChanged ? 'ring-1 ring-term-warning' : ''}`}>{s.tempo}</span>
                      </td>
                      <td className="text-center">
                        <span className={`font-bold text-[10px] ${b.threat === 'RED' ? 'text-term-error' : b.threat === 'AMBER' ? 'text-term-warning' : 'text-term-success'}`}>{b.threat}</span>
                      </td>
                      <td className="text-center">
                        <span className={`font-bold text-[10px] ${s.threat === 'RED' ? 'text-term-error' : s.threat === 'AMBER' ? 'text-term-warning' : 'text-term-success'} ${threatChanged ? 'underline' : ''}`}>{s.threat}</span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Comparison summary */}
          <div className="mt-3 grid grid-cols-4 gap-3">
            <BatchStat
              label="Avg Cap Delta"
              value={(() => {
                const deltas = comparisonResults.baseline.map((b, i) => (comparisonResults.scenario[i]?.capacity ?? b.capacity) - b.capacity)
                const avg = deltas.reduce((s, d) => s + d, 0) / deltas.length
                return `${avg > 0 ? '+' : ''}${avg.toFixed(1)}`
              })()}
              color={(() => {
                const deltas = comparisonResults.baseline.map((b, i) => (comparisonResults.scenario[i]?.capacity ?? b.capacity) - b.capacity)
                const avg = deltas.reduce((s, d) => s + d, 0) / deltas.length
                return avg < 0 ? 'text-term-error' : 'text-term-success'
              })()}
            />
            <BatchStat
              label="Tempo Changes"
              value={`${comparisonResults.baseline.filter((b, i) => b.tempo !== comparisonResults.scenario[i]?.tempo).length}/${comparisonResults.baseline.length}`}
              color="text-term-warning"
            />
            <BatchStat
              label="Threat Escalations"
              value={`${comparisonResults.baseline.filter((b, i) => {
                const s = comparisonResults.scenario[i]
                if (!s) return false
                const order = ['GREEN', 'AMBER', 'RED']
                return order.indexOf(s.threat) > order.indexOf(b.threat)
              }).length}/${comparisonResults.baseline.length}`}
              color="text-term-error"
            />
            <BatchStat
              label="RED Threats"
              value={`${comparisonResults.scenario.filter(s => s.threat === 'RED').length} (was ${comparisonResults.baseline.filter(b => b.threat === 'RED').length})`}
              color="text-term-error"
            />
          </div>
        </div>
      )}

      {/* Live prediction stress test — ALL ASSETS */}
      <div className="term-box p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-term-textDim font-medium uppercase tracking-wider">
            {activeScenario.id !== 'baseline' ? (
              <><span className={activeScenario.color}>{activeScenario.name}</span> — Live Prediction ({assets.length} assets)</>
            ) : (
              <>Stress Test — Live Prediction ({assets.length} assets)</>
            )}
          </div>
          <button
            onClick={runPredictAll}
            disabled={predictingAll || predicting || runningComparison}
            className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded
                       bg-term-primary/20 text-term-primary border border-term-primary/40
                       hover:bg-term-primary/30 transition-colors
                       disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {predictingAll ? 'Running...' : 'Predict All'}
          </button>
        </div>

        {/* Scrollable asset grid — shows ALL assets */}
        <div className="flex flex-wrap gap-2 max-h-[160px] overflow-y-auto pr-1">
          {assets.map(a => {
            const hasDetail = !!assetDetails[a.id]
            const isActive = predictingAssetId === a.id
            return (
              <button
                key={a.id}
                onClick={() => runPrediction(a)}
                disabled={predicting || predictingAll || !hasDetail}
                className={`term-box p-2 text-xs text-center cursor-pointer
                           hover:bg-term-bgLight/50 transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed
                           min-w-[100px] flex-shrink-0
                           ${isActive ? 'ring-1 ring-term-primary' : ''}`}
              >
                <div className="font-bold text-term-text truncate text-[11px]">{a.asset_type}</div>
                <div className="text-term-textDim text-[10px]">{formatPercent(a.mc_rate)} MC</div>
                <div className="text-term-textDim text-[9px]">{a.assigned_count} assigned</div>
              </button>
            )
          })}
        </div>

        {/* Single prediction result */}
        {predictionResult && !allResults.length && (
          <div className="mt-3 term-box p-3 border-term-primary/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-term-text">{predictionResult.asset}</span>
              <span className="text-[10px] text-term-textDim font-mono">{predictionResult.time_ms.toFixed(1)}ms</span>
            </div>
            <div className="grid grid-cols-4 gap-3 text-xs">
              <div>
                <span className="text-term-textDim">Capacity:</span>{' '}
                <span className="text-term-text font-bold">{predictionResult.capacity.toFixed(1)}</span>
                <span className="text-term-textDim text-[9px] ml-1">
                  [{predictionResult.confidence_interval[0].toFixed(1)}, {predictionResult.confidence_interval[1].toFixed(1)}]
                </span>
              </div>
              <div>
                <span className="text-term-textDim">Tempo:</span>{' '}
                <span className="text-term-text font-bold">{predictionResult.tempo}</span>
                <span className="text-term-textDim text-[9px] ml-1">
                  ({formatPercent(predictionResult.tempoConfidence)})
                </span>
              </div>
              <div>
                <span className="text-term-textDim">Threat:</span>{' '}
                <span className={`font-bold ${predictionResult.threat === 'RED' ? 'text-term-error' : predictionResult.threat === 'AMBER' ? 'text-term-warning' : 'text-term-success'}`}>
                  {predictionResult.threat}
                </span>
              </div>
              <div>
                <span className="text-term-textDim">Model:</span>{' '}
                <span className="text-term-primary font-bold">{predictionResult.model}</span>
              </div>
            </div>

            {/* Expandable feature vector */}
            <button
              onClick={() => toggleFeatureExpand(predictionResult.assetId)}
              className="mt-2 text-[10px] text-term-textDim hover:text-term-primary transition-colors cursor-pointer"
            >
              {expandedFeatures === predictionResult.assetId ? '[-] Hide' : '[+] Show'} Feature Vector (20 vars)
            </button>
            {expandedFeatures === predictionResult.assetId && (
              <FeatureVectorTable features={predictionResult.features} />
            )}
          </div>
        )}
      </div>

      {/* Predict All — Summary Table */}
      {allResults.length > 0 && (
        <div className="term-box p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-term-textDim font-medium uppercase tracking-wider">
              Batch Prediction Results ({allResults.length}/{assets.length})
            </div>
            <div className="text-[10px] text-term-textDim font-mono">
              Total: {allResults.reduce((s, r) => s + r.time_ms, 0).toFixed(1)}ms
            </div>
          </div>

          <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
            <table className="term-table text-xs w-full">
              <thead className="sticky top-0 bg-term-bg">
                <tr>
                  <th className="text-left">Asset</th>
                  <th className="text-right">Capacity</th>
                  <th className="text-right">CI</th>
                  <th className="text-center">Tempo</th>
                  <th className="text-center">Threat</th>
                  <th className="text-right">Model</th>
                  <th className="text-right">Latency</th>
                  <th className="text-center">Vector</th>
                </tr>
              </thead>
              <tbody>
                {allResults.map(r => (
                  <Fragment key={r.assetId}>
                    <tr>
                      <td className="text-left font-bold text-term-text whitespace-nowrap">{r.asset}</td>
                      <td className="text-right tabular-nums font-bold">{r.capacity.toFixed(1)}</td>
                      <td className="text-right tabular-nums text-term-textDim text-[10px]">
                        [{r.confidence_interval[0].toFixed(1)}, {r.confidence_interval[1].toFixed(1)}]
                      </td>
                      <td className="text-center">
                        <span className={`${TEMPO_CONFIG[r.tempo as Tempo]?.cssClass ?? 'tempo-steady'} px-1.5 py-0.5 rounded text-[9px]`}>
                          {r.tempo}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className={`font-bold ${r.threat === 'RED' ? 'text-term-error' : r.threat === 'AMBER' ? 'text-term-warning' : 'text-term-success'}`}>
                          {r.threat}
                        </span>
                      </td>
                      <td className="text-right text-term-primary text-[10px]">{r.model}</td>
                      <td className="text-right tabular-nums text-term-textDim">{r.time_ms.toFixed(1)}ms</td>
                      <td className="text-center">
                        <button
                          onClick={() => toggleFeatureExpand(r.assetId)}
                          className="text-[9px] text-term-textDim hover:text-term-primary transition-colors cursor-pointer"
                        >
                          {expandedFeatures === r.assetId ? '[-]' : '[+]'}
                        </button>
                      </td>
                    </tr>
                    {expandedFeatures === r.assetId && (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <FeatureVectorTable features={r.features} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Batch summary stats */}
          <div className="mt-3 grid grid-cols-4 gap-3">
            <BatchStat
              label="Avg Capacity"
              value={(allResults.reduce((s, r) => s + r.capacity, 0) / allResults.length).toFixed(1)}
            />
            <BatchStat
              label="Avg Latency"
              value={`${(allResults.reduce((s, r) => s + r.time_ms, 0) / allResults.length).toFixed(1)}ms`}
            />
            <BatchStat
              label="Threat RED"
              value={`${allResults.filter(r => r.threat === 'RED').length}/${allResults.length}`}
              color="text-term-error"
            />
            <BatchStat
              label="SURGE Detected"
              value={`${allResults.filter(r => r.tempo === 'SURGE').length}/${allResults.length}`}
              color="text-term-warning"
            />
          </div>
        </div>
      )}
      </>
      )}
    </div>
  )
}

// ===== Feature Vector Table (expandable) =====
function FeatureVectorTable({ features }: { features: number[] }) {
  return (
    <div className="mt-1 p-2 bg-term-bg/50 rounded border border-term-bgLight/30 overflow-x-auto">
      <div className="grid grid-cols-4 gap-x-4 gap-y-0.5 text-[10px] font-mono">
        {features.map((val, i) => (
          <div key={i} className="flex justify-between gap-2">
            <span className="text-term-textDim truncate">[{i.toString().padStart(2, '0')}] {FEATURE_LABELS[i]}</span>
            <span className="text-term-text tabular-nums font-bold flex-shrink-0">{formatFeatureValue(val)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ===== Batch stat helper =====
function BatchStat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="term-box p-2 text-center">
      <div className="text-[9px] text-term-textDim uppercase">{label}</div>
      <div className={`text-sm font-bold tabular-nums ${color ?? 'text-term-text'}`}>{value}</div>
    </div>
  )
}

// ===== Format a feature value for display =====
function formatFeatureValue(v: number): string {
  if (v === 0) return '0'
  if (Math.abs(v) < 1) return v.toFixed(4)
  if (Math.abs(v) < 100) return v.toFixed(2)
  return v.toFixed(1)
}
